import React from "react";
import {
  ListBox,
  Popover,
  EmptyState,
  FieldLabel,
  FieldError,
  inputFieldStyles,
  useRootContainer,
  POPOVER_LIST_BOX_MAX_HEIGHT,
} from "@appsmith/wds";
import {
  Select as HeadlessSelect,
  ListBoxItem as HeadlessListBoxItem,
} from "react-aria-components";

import type { SelectProps } from "./types";
import { SelectTrigger } from "./SelectTrigger";

export const Select = (props: SelectProps) => {
  const {
    children,
    contextualHelp,
    emptyStateTitle,
    errorMessage,
    isDisabled,
    isLoading,
    isRequired,
    label,
    placeholder,
    size = "medium",
    ...rest
  } = props;
  const root = useRootContainer();

  // RAC Select never opens its popover on an EMPTY collection
  // (useSelectState gates open/toggle on collection.size !== 0) and, unlike
  // ComboBox, has no allowsEmptyCollection escape hatch. So the M3 empty
  // state ships as a single DISABLED sentinel item: the collection has size
  // 1 (the popover can open) but the row cannot be focused or selected.
  // Loading beats empty — no empty state while options are loading.
  const showEmptyState =
    Boolean(emptyStateTitle) &&
    !Boolean(isLoading) &&
    typeof children !== "function" &&
    React.Children.count(children) === 0;

  // NOTE (UX review): there is deliberately NO renderEmptyState here. RAC's
  // useSelectState hard-gates open/toggle on collection.size !== 0, so a
  // Select with an empty DYNAMIC collection (function children) can never
  // open its popover — a ListBox renderEmptyState would be unreachable dead
  // API. Dynamic-children consumers get no empty state; the sentinel below
  // covers static children, which is what the WDS widgets use.

  return (
    <HeadlessSelect
      className={inputFieldStyles.field}
      data-size={size}
      isRequired={isRequired}
      {...rest}
    >
      {({ isInvalid }) => (
        <>
          <FieldLabel
            contextualHelp={contextualHelp}
            isDisabled={isDisabled}
            isRequired={isRequired}
          >
            {label}
          </FieldLabel>
          <SelectTrigger
            isDisabled={isDisabled}
            isInvalid={isInvalid}
            isLoading={isLoading}
            placeholder={placeholder}
            size={size}
          />
          <FieldError>{errorMessage}</FieldError>
          <Popover
            UNSTABLE_portalContainer={root}
            maxHeight={POPOVER_LIST_BOX_MAX_HEIGHT}
          >
            <ListBox shouldFocusWrap>
              {showEmptyState ? (
                // Raw RAC item (not the WDS ListBoxItem wrapper) because the
                // wrapper clamps non-function children into one-line Text.
                <HeadlessListBoxItem
                  id="wds-select-empty-state"
                  isDisabled
                  textValue={emptyStateTitle as string}
                >
                  <EmptyState title={emptyStateTitle as string} />
                </HeadlessListBoxItem>
              ) : (
                children
              )}
            </ListBox>
          </Popover>
        </>
      )}
    </HeadlessSelect>
  );
};
